import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Calendar } from "lucide-react";

interface DateInputProps {
  value: string;
  onChange: (isoDate: string) => void;
  id?: string;
  required?: boolean;
  "data-testid"?: string;
}

function isoToDisplay(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

function displayToIso(display: string): string | null {
  const match = display.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) return null;
  const [, d, m, y] = match;
  const day = d.padStart(2, "0");
  const month = m.padStart(2, "0");
  const date = new Date(`${y}-${month}-${day}`);
  if (isNaN(date.getTime())) return null;
  return `${y}-${month}-${day}`;
}

export default function DateInput({ value, onChange, id, required, ...props }: DateInputProps) {
  const [display, setDisplay] = useState(() => isoToDisplay(value));
  const hiddenRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplay(isoToDisplay(value));
  }, [value]);

  function handleTextChange(text: string) {
    setDisplay(text);
    const iso = displayToIso(text);
    if (iso) {
      onChange(iso);
    }
  }

  function handleBlur() {
    const iso = displayToIso(display);
    if (iso) {
      onChange(iso);
    } else if (value) {
      setDisplay(isoToDisplay(value));
    }
  }

  return (
    <div className="relative">
      <Input
        id={id}
        type="text"
        placeholder="DD.MM.YYYY"
        value={display}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleBlur}
        required={required}
        data-testid={props["data-testid"]}
      />
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        onClick={() => hiddenRef.current?.showPicker?.()}
        tabIndex={-1}
      >
        <Calendar className="w-4 h-4" />
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={value}
        onChange={(e) => {
          if (e.target.value) {
            onChange(e.target.value);
          }
        }}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}
